import React, { JSX, useEffect, useState } from 'react';
import Select, { components, DropdownIndicatorProps, ValueContainerProps, OptionProps } from 'react-select';
import Styles from './ReactInputSelectV2.module.scss';
import { ERROR_COLOR } from '../../../utils/constants';
import { viewport } from '../../../utils/utils';
import { AssetPathPropConfig } from '../../../services/LineTableBasicPropConfig';

interface ReactInputSelectSchema {
  name: string;
  options: any[];
  content: any;
  optionsLabel?: string;
  optionsValue?: string;
  defaultValue: string | unknown;
  disableValues?: string[];
  isDisabled?: boolean;
  onChange?: (value: any) => void;
  isSearchable?: boolean;
  isClearable?: boolean;
  updateValue?: string | unknown;
  valueContainerVarient?: string;
  optionsContainerVarient?: string;
}

// eslint-disable-next-line max-lines-per-function
const ReactInputSelectV2 = React.forwardRef<any, ReactInputSelectSchema>(
  (
    {
      name,
      options,
      content,
      isSearchable = true,
      disableValues = [],
      defaultValue = '',
      optionsValue = 'value',
      optionsLabel = 'label',
      isDisabled = false,
      isClearable = true,
      onChange,
      valueContainerVarient = '',
      optionsContainerVarient = '',
      updateValue
    },
    ref
  ): JSX.Element => {
    const [hasInputclicked, setInputclicked] = useState<boolean>(false);
    const [selectedOption, setSelectedOption] = useState<any>(defaultValue);
    const [isFocus, setIsFocus] = useState<boolean>(false);
    const [isError, setError] = useState<boolean>(false);
    const assetPath = AssetPathPropConfig?.getInstance()?.getAssetPath();
    const viewPort = viewport();

    const handleChange = (value: any) => {
      setSelectedOption(value);
      if (typeof onChange === 'function') {
        if (value) onChange({ name: name, value: value });
        else onChange({ name: name, value: { [optionsValue]: '', [optionsLabel]: '' } });
      }
    };

    const DropdownIndicator = (props: DropdownIndicatorProps<any>): JSX.Element => {
      const Component = components.DropdownIndicator as React.ComponentType<any>;
      return (
        <Component {...props}>
          <span
            className={`${Styles.downArrowIcon} ${
              props.selectProps.menuIsOpen ? Styles.transform180 : ''
            } ${
              (valueContainerVarient === 'deviceStorageValue' ||
                valueContainerVarient === 'deviceColorValue') &&
              viewPort &&
              viewPort !== 'large-desktop' &&
              Styles.customDropdown
            }`}
            style={{ background: `url('${assetPath}/icons/chevron-down.svg') no-repeat` }}
          />
        </Component>
      );
    };

    const handleBlur = () => {
      !hasInputclicked && setInputclicked(true);
      setIsFocus(false);
    };

    if (Boolean(selectedOption) == isError) {
      setError(!isError);
    }

    const customStyles = (hasError: boolean) => ({
      control: (styles: any) => ({
        ...styles,
        ...(hasError && { border: '2px solid ' + ERROR_COLOR + ' !important' }),
        ...(valueContainerVarient === 'deviceModalValue' && {
          padding: '8px 0px',
          height: 'auto !important'
        })
      }),
      valueContainer: (styles: any) => ({
        ...styles,
        ...(!content.label && { padding: '0px 0px 0px 8px !important' })
      }),
      singleValue: (styles: any) => ({
        ...styles,
        ...(valueContainerVarient === 'deviceModalValue' && {
          whiteSpace: 'initial'
        })
      }),
      dropdownIndicator: (styles: any) => ({
        ...styles,
        ...(valueContainerVarient === 'deviceStorageValue' &&
          (viewPort === 'mobile' || viewPort === 'desktop') && { padding: '0px' })
      }),
      option: (styles: any, state: any) => ({
        ...styles,
        ...(state?.isSelected &&
          valueContainerVarient === 'deviceStorageValue' &&
          (viewPort === 'mobile' || viewPort === 'desktop') && {
            backgroundPositionX: 'calc(100% - 4px) !important'
          })
      })
    });

    const valueContainerHandler = ({ children, ...props }: ValueContainerProps<any>): JSX.Element => {
      const Component = components.ValueContainer as React.ComponentType<any>;
      return (
        <Component {...props}>
          {props?.getValue()?.[0]?.name && (
            <div className='d-flex align-items-center'>
              {valueContainerVarient === 'deviceModalValue' && (
                <img 
                  src={props?.getValue()?.[0]?.icon} 
                  width={48} 
                  height={48} 
                  className='me-2' 
                  alt='icon'
                />
              )}
              {valueContainerVarient === 'deviceColorValue' && (
                <div
                  className={Styles.deviceColorValue}
                  style={{
                    backgroundColor: `${props?.getValue()?.[0]?.code}`,
                    border:
                      props?.getValue()?.[0]?.id === 'Silver' ||
                      props?.getValue()?.[0]?.id === 'White'
                        ? '2px solid #E6E3DF'
                        : ''
                  }}
                />
              )}
              {children}
            </div>
          )}
        </Component>
      );
    };

    const optionsContainerHandler = (props: OptionProps<any>): JSX.Element => {
      const Component = components.Option as React.ComponentType<any>;
      return (
        <Component {...props}>
          <div
            className={`d-flex align-items-center ${props?.isFocused && Styles.highlightOption} ${
              props?.isSelected && Styles.shiftingTick
            }`}
          >
            {optionsContainerVarient === 'deviceColorOptions' && (
              <>
                <div
                  className={Styles.deviceColorValue}
                  style={{
                    backgroundColor: `${props?.data?.code}`,
                    border:
                      props?.data?.id === 'Silver' || props?.data?.id === 'White'
                        ? '2px solid #E6E3DF'
                        : ''
                  }}
                />
                {props?.data?.name}
              </>
            )}
          </div>
        </Component>
      );
    };

    useEffect(() => {
      if (updateValue) setSelectedOption(updateValue);
    }, [updateValue]);

    return (
      <div className={Styles.selectcontainer}>
        <Select
          className={`react-select-container ${isError && 'errorborder'}`}
          styles={customStyles(isError && hasInputclicked)}
          name={name}
          instanceId='React-select'
          classNamePrefix='react-select'
          components={{
            DropdownIndicator,
            IndicatorSeparator: null,
            ...(valueContainerVarient && { ValueContainer: valueContainerHandler }),
            ...(optionsContainerVarient && { Option: optionsContainerHandler })
          }}
          value={selectedOption}
          onChange={handleChange}
          isClearable={isClearable}
          placeholder={''}
          // onFocus={() => setIsFocus(true)}
          onBlur={handleBlur}
          backspaceRemovesValue={true}
          options={options}
          ref={ref}
          getOptionLabel={(option: any) => option[optionsLabel]}
          getOptionValue={(option: any) => option[optionsValue]}
          isOptionDisabled={(option: any) => disableValues.includes(option[optionsValue])}
          isDisabled={isDisabled}
          isSearchable={isSearchable}
        />
        <label
          className={`${
            ((selectedOption && selectedOption[optionsValue] && selectedOption[optionsLabel]) ||
              isFocus) &&
            'activeLabel'
          } selectcomplabel`}
        >
          {content.label}
        </label>
        {isError && hasInputclicked && (
          <span className={Styles.errormessage}>{content.errorMessage}</span>
        )}
      </div>
    );
  }
);

export default ReactInputSelectV2;
